import { StudentBirthday } from 'Src/components/StudentBirthday/StudentBirthday';
import * as React from 'react';
import propz from 'propz';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { TextCell } from '../Grid/Cells/TextCell';
import {
  ColumnDefinition,
  CustomCellColumnDefinition,
  FunctionAccessorColumnDefinition,
  getColSpanNumber,
  isCustomCellColumnDefinition,
  isFunctionAccessorBasedColumnDefinition,
  isPathAccessorBasedColumnDefinition,
  PathAccessorColumnDefinition,
  TABLE_SORT_DIRECTION
} from '../../helpers/table/table';
import { FilterRow2 } from '../Grid/FilterRow2';
import { FilterIcon } from '../FilterIcon/FilterIcon';
import { Pagination2 } from '../Pagination/Pagination2';
import { GridFilterButtons } from '../Grid/GridFilterButtons';
import { SelectAllCheckbox } from '../SelectAllCheckbox/SelectAllCheckbox';
import { GridSortIcon } from '../Grid/GridSortIcon';
import { SelectOptions } from '../Grid/Grid2';
import { getPersonListSortedByName } from 'Src/helpers/table/table';
import { useMobileColumns } from 'Src/hooks/useMobileColumns';
import { ColumnToggleButton } from '../ColumnToggleButton/ColumnToggleButton';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';
import moment from 'moment';
import { findTwoPanelItemIndex, getTwoPanelItemIdentity, isSameTwoPanelItem } from './itemIdentity';
import { SearchMultiSelectFilter } from '../SearchMultiSelectFilter/SearchMultiSelectFilter';
import { FilterChipSelect } from '../FilterChipSelect/FilterChipSelect';
import { GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';

interface Props {
  items: any[];
  itemsSelected: any[];
  title?: string;
  columns: ColumnDefinition[];
  onItemClick: (index: number, item?: any) => void;
  onFilterChange?: (event, filterField: string, options?) => void;
  filters?: any;
  filterOptions?: SelectOptions;
  filterLabels?: Record<string, string>;
  useRecipientFilterLayout?: boolean;
  isShowFilter?: boolean;
  isFiltered?: boolean;
  onFilterClick?: (event) => void;

  total: number;
  currentPage: number;
  paginationLimit?: number;

  setCurrentPageParams: (currentPage: number) => void;

  onApplyFilterClick: () => void;
  onClearFilterClick: () => void;

  onSelectAllClick: () => void;
  onSelectAllOnPageClick: () => void;
  onSelectVisibleItems?: (items: any[]) => void;
  onUnselectAllClick: () => void;
  isSelectAllChecked: boolean;
  isNoVisibleBlock?: boolean;

  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  onSortClick: (sortField: string) => void;

  itemsAddBuffer?: any[];
  itemsRemoveBuffer?: any[];

  onCellClick?: (item) => void;
  removeItemFromBuffer?: (itemId: string) => void;

  onEditClick?: (item: any) => void;

  onItemDoubleClick?: (index: number, item?: any) => void;
  isLoading?: boolean;
  loadingMessage?: string;

  /** All column definitions (including hidden), for the toggle dropdown */
  allColumns?: ColumnDefinition[];
  /** Set of field names currently hidden by user preference */
  hiddenFields?: Set<string>;
  /** Called when user toggles a column in the dropdown */
  onToggleColumn?: (field: string) => void;
  /** Whether a field can be toggled (not locked) */
  isColumnToggleable?: (field: string) => boolean;
  onResetColumns?: () => void;
  isSourceFirst?: boolean;
  unitLabel?: string;
  presetFilters?: Record<string, string[]>;
  presetSourceLabel?: string;
  lockPresetFilters?: boolean;
  onReplaceFilters?: (filters: any) => void;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  onSelectAllFilteredClick?: () => void;
  showAvatars?: boolean;
  alwaysShowSelectAllLink?: boolean;
  allowSelectAllFiltered?: boolean;
  modernPagination?: boolean;
  hiddenFilterChipFields?: string[];
  capacity?: number;
  clashEventsById?: Record<string, any>;
  showFilterControl?: boolean;
  headerExtra?: React.ReactNode;
  searchExtra?: React.ReactNode;
  searchByNameOnly?: boolean;
}

export const Panel: FunctionComponent<Props> = props => {
  const {
    items,
    title,
    columns,
    itemsSelected,
    onItemClick,
    onFilterChange,
    filters,
    filterOptions,
    filterLabels = {},
    useRecipientFilterLayout = false,
    isShowFilter,
    isFiltered,
    onFilterClick,
    total,
    currentPage,
    setCurrentPageParams,
    onApplyFilterClick,
    onClearFilterClick,
    paginationLimit,
    onSelectAllClick,
    onSelectAllOnPageClick,
    onSelectVisibleItems,
    onUnselectAllClick,
    isSelectAllChecked,
    sortDirection,
    sortColumnsName,
    onSortClick,
    itemsAddBuffer,
    itemsRemoveBuffer,
    onCellClick,
    isNoVisibleBlock,
    removeItemFromBuffer,
    onEditClick,
    onItemDoubleClick,
    isLoading,
    loadingMessage,
    allColumns,
    hiddenFields,
    onToggleColumn,
    isColumnToggleable,
    isSourceFirst,
    unitLabel = 'items',
    presetFilters = {},
    presetSourceLabel = 'event',
    lockPresetFilters = true,
    onReplaceFilters,
    onSearchChange,
    searchValue,
    onSelectAllFilteredClick,
    showAvatars = true,
    modernPagination = false,
    hiddenFilterChipFields = [],
    capacity,
    clashEventsById = {},
    showFilterControl = true,
    headerExtra,
    searchExtra,
    searchByNameOnly = false
  } = props;

  const visibleColumns = useMobileColumns(columns);
  const [search, setSearch] = useState(searchValue || '');
  const [appliedSearch, setAppliedSearch] = useState(searchValue || '');
  const [optionSearches, setOptionSearches] = useState<Record<string, string>>({});
  const [savedFilterPresets, setSavedFilterPresets] = useState<Array<{ name: string; filters: any }>>([]);
  const [draftFilters, setDraftFilters] = useState<any>({});
  const [isSaveFilterModalOpen, setIsSaveFilterModalOpen] = useState(false);
  const [filterTemplateName, setFilterTemplateName] = useState('');
  const [filterTemplateNameError, setFilterTemplateNameError] = useState('');
  const [filterTemplateSnapshot, setFilterTemplateSnapshot] = useState<any>({});
  const [filterTemplateSuggestion, setFilterTemplateSuggestion] = useState('');
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(new Set());
  const [expandedChipGroups, setExpandedChipGroups] = useState<Set<string>>(new Set());
  const filterAnchorRef = useRef<HTMLDivElement>(null);
  const previousAddedIdsRef = useRef<Set<string>>(new Set());
  const resetDraftFilters = () => setDraftFilters(JSON.parse(JSON.stringify(presetFilters || {})));

  useEffect(() => {
    if (typeof searchValue === 'string' && searchValue !== appliedSearch) {
      setSearch(searchValue);
      setAppliedSearch(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (!isShowFilter) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const anchor = filterAnchorRef.current;
      if (!anchor || anchor.getClientRects().length === 0) return;
      if (!anchor.contains(event.target as Node)) {
        onFilterClick && onFilterClick({ preventDefault: () => {} });
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onFilterClick && onFilterClick({ preventDefault: () => {} });
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isShowFilter, onFilterClick]);

  useEffect(() => {
    if (isShowFilter) setDraftFilters(JSON.parse(JSON.stringify(filters || {})));
  }, [isShowFilter]);

  useEffect(() => {
    const currentIds = new Set(
      (itemsAddBuffer || []).map(getTwoPanelItemIdentity).filter((identity): identity is string => Boolean(identity))
    );
    const newIds = Array.from(currentIds).filter(id => !previousAddedIdsRef.current.has(id));
    previousAddedIdsRef.current = currentIds;
    if (!newIds.length) return;
    setRecentlyAddedIds(new Set(newIds));
    const timer = window.setTimeout(() => setRecentlyAddedIds(new Set()), 2200);
    return () => window.clearTimeout(timer);
  }, [itemsAddBuffer]);

  const normalizedSearch = appliedSearch.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (onSearchChange || !normalizedSearch) return items;
    return items.filter(item => {
      const nameFields = [item.firstName, item.lastName, item.name];
      const studentContextFields = [
        item.yearGroup,
        propz.get(item, ['form', 'name'], ''),
        propz.get(item, ['form', 'ageGroup'], ''),
        propz.get(item, ['house', 'name'], '')
      ];

      return (searchByNameOnly ? nameFields : [...nameFields, ...studentContextFields])
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [items, normalizedSearch, onSearchChange, searchByNameOnly]);

  const sortClick = (event, field) => {
    event.preventDefault();
    onSortClick(field);
  };

  const renderAvatar = (item: any) => {
    const nameParts = String(item.name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const initials = (
      `${String(item.firstName || '').charAt(0)}${String(item.lastName || '').charAt(0)}` ||
      `${String(nameParts[0] || '').charAt(0)}${String(nameParts[nameParts.length - 1] || '').charAt(0)}`
    ).toUpperCase();
    const avatar =
      item.avatar || item.pic || propz.get(item, ['profile', 'avatar'], '') || propz.get(item, ['picUrl'], '');
    const paletteIndex =
      String(item.id || initials)
        .split('')
        .reduce((total, char) => total + char.charCodeAt(0), 0) % 5;
    const houseColors = propz.get<any[]>(item, ['house', 'colors'], []) || [];
    const houseColor = houseColors[0] || propz.get(item, ['house', 'color'], '');

    return (
      <span
        className={`twoPanelEditorPanel__avatar is-palette-${paletteIndex}`}
        style={houseColor && !avatar ? { backgroundColor: houseColor } : undefined}
        aria-hidden="true"
      >
        <span>{initials || '?'}</span>
        {avatar && <img src={avatar} alt="" />}
      </span>
    );
  };

  const renderHead = visibleColumns.map((column, index) => {
    const { isSort, text, field } = column;

    if (isSort) {
      return (
        <th key={`header-${field}`}>
          <a className="eHeadSort" href="" onClick={event => sortClick(event, field)}>
            {`${text} `}
            <GridSortIcon
              tableSortDirection={sortDirection}
              tableSortColumnsName={sortColumnsName}
              tableColumnName={field}
            />
          </a>
        </th>
      );
    } else {
      return <th key={`header-${field}`}>{`${text}`}</th>;
    }
  });

  const renderItem = (item: any, visibleColumns: ColumnDefinition[]) => {
    const rowCells = visibleColumns.map(column => {
      switch (true) {
        case isPathAccessorBasedColumnDefinition(column): {
          const pathAccessorBasedColumn = column as PathAccessorColumnDefinition;
          const value = propz.get<any>(item, pathAccessorBasedColumn.accessor, '');
          const strValue = String(value);
          return <TextCell key={column.field} text={strValue} />;
        }

        case isFunctionAccessorBasedColumnDefinition(column): {
          const funcAccessorColumn = column as FunctionAccessorColumnDefinition;
          const value = funcAccessorColumn.accessor(item);
          const strValue = String(value);
          return <TextCell key={column.field} text={strValue} />;
        }

        case isCustomCellColumnDefinition(column): {
          const cellColumn = column as CustomCellColumnDefinition;
          const cell = cellColumn.cell(item, onCellClick);
          if (Array.isArray(cell)) {
            return (
              <React.Fragment key={column.field}>
                {cell.map((child, idx) => React.cloneElement(child, { key: `${column.field}-${idx}` }))}
              </React.Fragment>
            );
          }
          return <td key={column.field}>{cell}</td>;
        }

        default: {
          console.error('Not supported column type: ' + JSON.stringify(column, null, 2));
          return null;
        }
      }
    });

    return rowCells;
  };

  const sortedItems = getPersonListSortedByName(filteredItems);

  const renderBody = sortedItems.map((item, sortedIndex) => {
    const originalIndex = findTwoPanelItemIndex(items, item, sortedIndex);
    const itemIdentity = getTwoPanelItemIdentity(item);
    const isItemSelected = itemsSelected.some(itemSelected => isSameTwoPanelItem(itemSelected, item));
    const isItemRemoved = (itemsRemoveBuffer || []).some(itemRemove => isSameTwoPanelItem(itemRemove, item));
    const isItemAdded = (itemsAddBuffer || []).some(itemAdd => isSameTwoPanelItem(itemAdd, item));

    return (
      <tr
        key={itemIdentity || `item-${originalIndex}`}
        className={[
          'twoPanelEditorPanel__row',
          isItemSelected ? 'is-selected' : '',
          isItemAdded ? 'is-pending-addition' : '',
          isItemRemoved ? 'is-pending-removal' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => !isItemRemoved && onItemClick(originalIndex, item)}
        onDoubleClick={() => !isItemRemoved && onItemDoubleClick && onItemDoubleClick(originalIndex, item)}
      >
        <td className="twoPanelEditorPanel__checkboxCell">
          {!isItemRemoved && <input onChange={() => {}} type="checkbox" checked={isItemSelected} />}
        </td>
        {isSourceFirst && <td className="twoPanelEditorPanel__avatarCell">{renderAvatar(item)}</td>}
        {renderItem(item, visibleColumns)}
      </tr>
    );
  });

  const renderBodyAddBuffer = itemsAddBuffer.map((item, index) => (
    <tr
      key={getTwoPanelItemIdentity(item) || `added-item-${index}`}
      className="twoPanelEditorPanel__row is-pending-addition"
    >
      <td className="twoPanelEditorPanel__checkboxCell">
        {removeItemFromBuffer && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              removeItemFromBuffer(item.id);
            }}
            className="twoPanelEditorPanel__removeBtn"
            title="Cancel pending addition"
          >
            ×
          </button>
        )}
      </td>
      {isSourceFirst && <td className="twoPanelEditorPanel__avatarCell">{renderAvatar(item)}</td>}
      {renderItem(item, visibleColumns)}
    </tr>
  ));

  const colSpanNumber = getColSpanNumber(visibleColumns) + (isSourceFirst ? 1 : 0);

  if (isSourceFirst) {
    const isAvailable = !isNoVisibleBlock;
    const isCapacityExceeded = !isAvailable && typeof capacity === 'number' && capacity > 0 && total > capacity;
    const showClashes = columns.some(column => column.field === 'clashes');
    const workingFilters = filters || {};
    const drawerFilters = isShowFilter ? draftFilters : workingFilters;
    const unorderedFilterDefinitions = [
      { key: 'houses', label: filterLabels.houses || 'House', options: (filterOptions && filterOptions.houses) || [] },
      { key: 'forms', label: filterLabels.forms || 'Form', options: (filterOptions && filterOptions.forms) || [] },
      { key: 'ages', label: filterLabels.ages || 'Year group', options: (filterOptions && filterOptions.ages) || [] },
      {
        key: 'gender',
        label: filterLabels.gender || 'Gender',
        options: (filterOptions && (filterOptions as any).gender) || []
      },
      {
        key: 'permissionFormIds',
        label: filterLabels.permissionFormIds || 'Form access',
        options: (filterOptions && (filterOptions as any).permissionFormIds) || []
      },
      {
        key: 'permissionHouseIds',
        label: filterLabels.permissionHouseIds || 'House access',
        options: (filterOptions && (filterOptions as any).permissionHouseIds) || []
      },
      {
        key: 'permissionSportIds',
        label: 'Sports',
        options: (filterOptions && (filterOptions as any).permissionSportIds) || []
      },
      { key: 'role', label: 'Role', options: (filterOptions && (filterOptions as any).role) || [] },
      { key: 'tag', label: filterLabels.tag || 'Tag', options: (filterOptions && (filterOptions as any).tag) || [] },
      { key: 'status', label: 'Invitation status', options: (filterOptions && (filterOptions as any).status) || [] },
      { key: 'priority', label: 'Priority', options: (filterOptions && (filterOptions as any).priority) || [] }
    ]
      .map(definition => ({
        ...definition,
        options: definition.options.filter(
          option =>
            option &&
            String(typeof option.text === 'undefined' ? '' : option.text).trim() !== '' &&
            String(typeof option.value === 'undefined' ? '' : option.value).trim() !== ''
        )
      }))
      .filter(definition => definition.options.length > 0);
    const hasStudentRecipientFilters = unorderedFilterDefinitions.some(definition => definition.key === 'ages');
    const recipientFilterOrder: Record<string, number> = hasStudentRecipientFilters
      ? { ages: 0, houses: 1, forms: 2, gender: 3, tag: 4 }
      : { permissionHouseIds: 0, permissionFormIds: 1, tag: 2, role: 3, permissionSportIds: 4 };
    const filterDefinitions = useRecipientFilterLayout
      ? [...unorderedFilterDefinitions].sort((first, second) => {
          const firstOrder = recipientFilterOrder[first.key];
          const secondOrder = recipientFilterOrder[second.key];
          const normalizedFirstOrder = typeof firstOrder === 'number' ? firstOrder : Number.MAX_SAFE_INTEGER;
          const normalizedSecondOrder = typeof secondOrder === 'number' ? secondOrder : Number.MAX_SAFE_INTEGER;
          return normalizedFirstOrder - normalizedSecondOrder;
        })
      : unorderedFilterDefinitions;
    const activeValuesForSource = (definition: any, sourceFilters: any): any[] => {
      if (hiddenFilterChipFields.includes(definition.key)) return [];
      const values = Array.isArray(sourceFilters && sourceFilters[definition.key]) ? sourceFilters[definition.key] : [];
      if (
        definition.key === 'gender' &&
        definition.options.length > 0 &&
        values.length === definition.options.length &&
        (presetFilters.gender || []).length === 0
      ) {
        return [];
      }
      return values;
    };
    const activeValuesFor = (definition: any): any[] => activeValuesForSource(definition, workingFilters);
    const draftActiveValuesFor = (definition: any): any[] => activeValuesForSource(definition, drawerFilters);
    const attributeFilterCount = filterDefinitions.reduce(
      (count, definition) => count + activeValuesFor(definition).length,
      0
    );
    const birthdayFilter = (workingFilters && workingFilters.birthday) || {};
    const hasBirthdayFilter = columns.some(column => column.field === 'birthday');
    const hasSenFilter = columns.some(column => column.field === 'specialEducationNeeds');
    const hasPupilPremiumFilter = columns.some(column => column.field === 'pupilsPremium');
    const getDateFilterValue = (value: string): Date | null => {
      const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
      return parsed.isValid() ? parsed.toDate() : null;
    };
    const activeFilterCount =
      attributeFilterCount +
      (hasBirthdayFilter && birthdayFilter.from ? 1 : 0) +
      (hasBirthdayFilter && birthdayFilter.to ? 1 : 0) +
      (hasSenFilter && workingFilters && workingFilters.specialEducationNeeds ? 1 : 0) +
      (hasPupilPremiumFilter && workingFilters && workingFilters.pupilsPremium ? 1 : 0);
    const drawerBirthdayFilter = (drawerFilters && drawerFilters.birthday) || {};
    const drawerFilterCount =
      filterDefinitions.reduce((count, definition) => count + draftActiveValuesFor(definition).length, 0) +
      (hasBirthdayFilter && drawerBirthdayFilter.from ? 1 : 0) +
      (hasBirthdayFilter && drawerBirthdayFilter.to ? 1 : 0) +
      (hasSenFilter && drawerFilters && drawerFilters.specialEducationNeeds ? 1 : 0) +
      (hasPupilPremiumFilter && drawerFilters && drawerFilters.pupilsPremium ? 1 : 0);
    const filtersDifferFromPreset = filterDefinitions.some(definition => {
      const current = activeValuesFor(definition)
        .map(String)
        .sort();
      const initial = (presetFilters[definition.key] || []).map(String).sort();
      return current.length !== initial.length || current.some((value, index) => value !== initial[index]);
    });
    const saveCurrentFilter = () => {
      if (!drawerFilterCount) {
        return;
      }
      setFilterTemplateName('');
      setFilterTemplateNameError('');
      setFilterTemplateSnapshot(JSON.parse(JSON.stringify(drawerFilters || {})));
      setFilterTemplateSuggestion(suggestedFilterName);
      setIsSaveFilterModalOpen(true);
    };
    const activeFilterLabels = filterDefinitions.reduce<string[]>((labels, definition) => {
      const definitionLabels = draftActiveValuesFor(definition).map(value => {
        const option = definition.options.find(candidate => String(candidate.value) === String(value));
        return option ? String(option.text) : String(value);
      });
      return labels.concat(definitionLabels);
    }, []);
    const suggestedFilterName = [
      ...activeFilterLabels,
      drawerBirthdayFilter.from || drawerBirthdayFilter.to
        ? `DOB ${drawerBirthdayFilter.from || 'any'}–${drawerBirthdayFilter.to || 'any'}`
        : '',
      drawerFilters.specialEducationNeeds ? 'SEN' : '',
      drawerFilters.pupilsPremium ? 'Pupil premium' : ''
    ]
      .filter(Boolean)
      .join(' · ');
    const confirmSaveCurrentFilter = () => {
      const name = filterTemplateName.trim();
      if (!name) {
        setFilterTemplateNameError('Enter a name for this filter.');
        return;
      }
      if (savedFilterPresets.some(preset => preset.name.toLowerCase() === name.toLowerCase())) {
        setFilterTemplateNameError('A saved filter with this name already exists.');
        return;
      }
      setSavedFilterPresets(current => [...current, { name, filters: filterTemplateSnapshot }]);
      setIsSaveFilterModalOpen(false);
    };
    const toggleFilterValue = (definition: any, value: string) => {
      const current = new Set<string>((drawerFilters && drawerFilters[definition.key]) || []);
      current.has(value) ? current.delete(value) : current.add(value);
      const nextFilters = { ...(drawerFilters || {}), [definition.key]: Array.from(current) };
      setDraftFilters(nextFilters);
      if (!isShowFilter && onReplaceFilters) onReplaceFilters(nextFilters);
    };
    const renderFilterDefinition = (definition: any): React.ReactNode => {
      if (definition.options.length === 0) return null;

      const selectedValues = new Set<string>(
        ((drawerFilters && drawerFilters[definition.key]) || []).map(value => String(value))
      );
      const isPresetOption = (value: any): boolean =>
        lockPresetFilters && (presetFilters[definition.key] || []).map(String).includes(String(value));
      const presetNote = (presetFilters[definition.key] || []).length > 0 && (
        <p className="twoPanelEditorPanel__presetNote">
          {lockPresetFilters && (
            <svg className="twoPanelEditorPanel__optionLock" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="5.5" y="10" width="13" height="10" rx="2" />
              <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" />
            </svg>
          )}
          {lockPresetFilters
            ? `Preset from ${presetSourceLabel} setup — tick more to include exceptions`
            : `Initially selected from ${presetSourceLabel} setup — untick to narrow or tick to expand`}
        </p>
      );
      const isSearchMultiFilter =
        definition.key === 'permissionFormIds' ||
        (definition.key === 'forms' && !/year group|age group/i.test(String(definition.label)));
      const isHouseOrTagFilter = ['houses', 'permissionHouseIds', 'tag'].includes(definition.key);
      const isAgeGroupFilter =
        definition.key === 'ages' ||
        (definition.key === 'forms' && /year group|age group/i.test(String(definition.label)));
      const isChipFilter =
        isHouseOrTagFilter ||
        (useRecipientFilterLayout && ['ages', 'gender'].includes(definition.key)) ||
        isAgeGroupFilter;

      if (isSearchMultiFilter) {
        return (
          <div key={definition.key} className="twoPanelEditorPanel__recipientSearchFilter">
            {presetNote}
            <SearchMultiSelectFilter
              label={definition.label}
              selected={Array.from(selectedValues)}
              options={definition.options.map(option => ({
                value: String(option.value),
                label: String(option.text || option.value)
              }))}
              disabledValues={definition.options
                .filter(option => isPresetOption(option.value))
                .map(option => String(option.value))}
              onToggle={value => toggleFilterValue(definition, value)}
              placeholder="Search registration forms…"
            />
          </div>
        );
      }

      if (isChipFilter) {
        const isHouseFilter = ['houses', 'permissionHouseIds'].includes(definition.key);
        return (
          <FilterChipSelect
            key={definition.key}
            className="twoPanelEditorPanel__recipientChipFilter"
            label={definition.label}
            hint={presetNote}
            selected={Array.from(selectedValues)}
            collapsedLimit={isHouseFilter || definition.key === 'tag' ? 6 : isAgeGroupFilter ? 4 : undefined}
            disabledValues={definition.options
              .filter(option => isPresetOption(option.value))
              .map(option => String(option.value))}
            options={definition.options.map(option => ({
              value: String(option.value),
              label: String(option.text || option.value),
              color: definition.key === 'tag' ? option.color : undefined
            }))}
            onToggle={value => toggleFilterValue(definition, value)}
          />
        );
      }

      return (
        <fieldset key={definition.key}>
          <legend>{definition.label}</legend>
          {presetNote}
          {definition.options.length > 6 && (
            <input
              className="twoPanelEditorPanel__optionSearch"
              value={optionSearches[definition.key] || ''}
              onChange={event => setOptionSearches(current => ({ ...current, [definition.key]: event.target.value }))}
              placeholder={`Search ${definition.label.toLowerCase()}…`}
            />
          )}
          <div>
            {definition.options.map(option => {
              const isVisible = String(option.text)
                .toLowerCase()
                .includes((optionSearches[definition.key] || '').toLowerCase());
              const isLocked = isPresetOption(option.value);
              return (
                <label key={String(option.value)} style={{ display: isVisible ? undefined : 'none' }}>
                  <input
                    type="checkbox"
                    checked={selectedValues.has(String(option.value))}
                    onChange={() => toggleFilterValue(definition, String(option.value))}
                    disabled={isLocked}
                  />{' '}
                  <span>{option.text}</span>
                  {isLocked && (
                    <svg
                      className="twoPanelEditorPanel__optionLock"
                      viewBox="0 0 24 24"
                      aria-label={`Set by ${presetSourceLabel}`}
                    >
                      <rect x="5.5" y="10" width="13" height="10" rx="2" />
                      <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" />
                    </svg>
                  )}
                </label>
              );
            })}
          </div>
        </fieldset>
      );
    };
    const clashInfo = (item: any) => {
      const fullEvents = propz.get<any[]>(item, ['clashes', 'fullClashEvents'], []) || [];
      const intervalEvents = propz.get<any[]>(item, ['clashes', 'intervalClashEvents'], []) || [];
      const events = [...fullEvents, ...intervalEvents].filter(
        (event, index, all) => all.findIndex(candidate => String(candidate._id) === String(event._id)) === index
      );
      const status = fullEvents.length
        ? 'FULL_CLASH'
        : intervalEvents.length
        ? 'INTERVAL_CLASH'
        : propz.get(item, ['clashes', 'status'], 'NO_CLASH');
      const eventName = events.length
        ? events
            .map(eventReference => {
              const event = clashEventsById[String(eventReference._id || eventReference.id)] || eventReference;
              const generatedNames = event.generatedNames || {};
              const generatedName =
                event.generatedName ||
                generatedNames.official ||
                Object.keys(generatedNames)
                  .map(key => generatedNames[key])
                  .find(Boolean);
              const time = event.startTime
                ? `${moment(event.startTime).format('HH:mm')}–${moment(event.endTime).format('HH:mm')}`
                : '';
              const displayTitle =
                event.displayTitle ||
                event.title ||
                event.name ||
                generatedName ||
                event.sportName ||
                event.sport?.name;
              return [displayTitle || 'Scheduled event', time].filter(Boolean).join(', ');
            })
            .filter(Boolean)
            .join(' · ')
        : '';
      if (status === 'FULL_CLASH') return { className: 'is-full', label: eventName || 'Clash' };
      if (status === 'INTERVAL_CLASH') return { className: 'is-partial', label: eventName || 'Possible clash' };
      return { className: 'is-clear', label: 'Free' };
    };
    const focusSibling = (event: React.KeyboardEvent<HTMLElement>, direction: number) => {
      const rows = Array.from(
        event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[data-transfer-row]') || []
      );
      const index = rows.indexOf(event.currentTarget);
      rows[index + direction]?.focus();
    };
    const displayedItems = isAvailable
      ? filteredItems
      : [...filteredItems].sort((a, b) => {
          const aRecent = recentlyAddedIds.has(getTwoPanelItemIdentity(a) || '');
          const bRecent = recentlyAddedIds.has(getTwoPanelItemIdentity(b) || '');
          return Number(bRecent) - Number(aRecent);
        });
    const renderTransferRow = (item: any, visibleIndex: number) => {
      const originalIndex = findTwoPanelItemIndex(items, item, visibleIndex);
      const itemIdentity = getTwoPanelItemIdentity(item);
      const checked = itemsSelected.some(candidate => isSameTwoPanelItem(candidate, item));
      const clash = clashInfo(item);
      const transferMeta = propz.get(item, ['transferMeta'], '');
      const meta = [
        item.schoolName || propz.get(item, ['school', 'name'], ''),
        propz.get(item, ['form', 'name'], ''),
        transferMeta || item.yearGroup || propz.get(item, ['form', 'ageGroup'], ''),
        GENDER_SERVER_TO_CLIENT_MAPPING[item.gender] || item.gender || '',
        propz.get(item, ['house', 'name'], '')
      ]
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index)
        .join(' · ');
      const eligibilityWarning = propz.get(item, ['eligibilityWarning'], '');
      const tournamentEvents = propz.get(item, ['tournamentEvents'], '');
      const displayName = item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim();

      return (
        <div
          key={itemIdentity || `item-${originalIndex}`}
          data-transfer-row
          tabIndex={0}
          className={`twoPanelEditorPanel__personRow${showClashes ? '' : ' withoutClashes'}${
            showAvatars ? '' : ' withoutAvatar'
          }${checked ? ' is-selected' : ''}${eligibilityWarning ? ' is-warning' : ''}${
            recentlyAddedIds.has(itemIdentity || '') ? ' is-recent' : ''
          }${onEditClick ? ' has-edit-action' : ''}`}
          title={isAvailable ? 'Double-click to add' : 'Double-click to remove'}
          onClick={() => onItemClick(originalIndex, item)}
          onDoubleClick={() => onItemDoubleClick && onItemDoubleClick(originalIndex, item)}
          onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              focusSibling(event, event.key === 'ArrowDown' ? 1 : -1);
            } else if (event.key === ' ') {
              event.preventDefault();
              onItemClick(originalIndex, item);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              onItemDoubleClick && onItemDoubleClick(originalIndex, item);
            }
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onItemClick(originalIndex, item)}
            onClick={event => event.stopPropagation()}
            aria-label={`Select ${displayName}`}
          />
          {showClashes && (
            <button
              type="button"
              className={`twoPanelEditorPanel__clashDot ${clash.className}`}
              title={clash.label}
              onClick={event => {
                event.stopPropagation();
                if (clash.className !== 'is-clear' && onCellClick) onCellClick(item);
              }}
              aria-label={clash.label}
            />
          )}
          {showAvatars && renderAvatar(item)}
          <span className="twoPanelEditorPanel__personText">
            <strong>{displayName}</strong>
            <small className={transferMeta ? 'has-details' : undefined} title={meta || undefined}>
              {meta || unitLabel.replace(/s$/i, '')}
            </small>
            {columns.some(column => column.field === 'birthday') && <span><StudentBirthday birthday={item.birthday} labelled /></span>}
            {tournamentEvents && (
              <small className="has-details" title={tournamentEvents}>
                Also in: {tournamentEvents}
              </small>
            )}
            {showClashes && clash.className !== 'is-clear' && <small className={clash.className}>{clash.label}</small>}
            {eligibilityWarning && <small className="is-warning">⚠ {eligibilityWarning}</small>}
          </span>
          {onEditClick && (
            <button
              type="button"
              className="twoPanelEditorPanel__rowEdit"
              onClick={event => {
                event.stopPropagation();
                onEditClick(item);
              }}
              aria-label={`Edit ${displayName}`}
              title={`Edit ${displayName}`}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="twoPanelEditorPanel__rowTransfer"
            onClick={event => {
              event.stopPropagation();
              onItemDoubleClick && onItemDoubleClick(originalIndex, item);
            }}
            aria-label={isAvailable ? 'Add participant' : 'Remove participant'}
          >
            {isAvailable ? '→' : '←'}
          </button>
        </div>
      );
    };

    return (
      <div className={`twoPanelEditorPanel twoPanelEditorPanel--${isAvailable ? 'available' : 'selected'}`}>
        <SimpleModal
          isOpen={isSaveFilterModalOpen}
          title="Save filter"
          onCloseClick={() => setIsSaveFilterModalOpen(false)}
          onBackdropClick={() => setIsSaveFilterModalOpen(false)}
          dialogStyle={{ maxWidth: 460 }}
          showFooter={false}
          isBodyBold={false}
          customClass="transferPickerSaveFilterModal"
          zIndex={1080}
        >
          <form
            className="transferPickerSaveFilter"
            onSubmit={event => {
              event.preventDefault();
              confirmSaveCurrentFilter();
            }}
          >
            <p>Give this filter a clear name so you can quickly apply the same selection later.</p>
            <label>
              <span>Filter name</span>
              <input
                autoFocus
                value={filterTemplateName}
                placeholder={filterTemplateSuggestion || 'e.g. Year 8 · Girls'}
                onChange={event => {
                  setFilterTemplateName(event.target.value);
                  if (filterTemplateNameError) setFilterTemplateNameError('');
                }}
                onKeyDown={event => event.key === 'Escape' && setIsSaveFilterModalOpen(false)}
              />
              {filterTemplateNameError && <small>{filterTemplateNameError}</small>}
              {!filterTemplateNameError && filterTemplateSuggestion && (
                <small className="transferPickerSaveFilter__suggestion">
                  Suggested:{' '}
                  <button type="button" onClick={() => setFilterTemplateName(filterTemplateSuggestion)}>
                    {filterTemplateSuggestion}
                  </button>
                </small>
              )}
            </label>
            <div className="transferPickerSaveFilter__actions">
              <button type="button" onClick={() => setIsSaveFilterModalOpen(false)}>
                Cancel
              </button>
              <button type="submit">Save filter</button>
            </div>
          </form>
        </SimpleModal>
        <div className="twoPanelEditorPanel__modernHead" ref={filterAnchorRef}>
          <div className="twoPanelEditorPanel__modernTitle">
            <strong>{title}</strong>
            <span>{isAvailable ? `${total} total` : total}</span>
          </div>
          {headerExtra && <div className="twoPanelEditorPanel__headerExtra">{headerExtra}</div>}
          {!isAvailable && typeof capacity === 'number' && capacity > 0 && (
            <div
              className={`twoPanelEditorPanel__capacity${isCapacityExceeded ? ' is-over-limit' : ''}`}
              role="status"
              aria-live="polite"
            >
              <span>
                {total} / {capacity} spaces filled
                {isCapacityExceeded && <strong> · {total - capacity} over limit</strong>}
              </span>
              <div>
                <i style={{ width: `${Math.min(100, (total / capacity) * 100)}%` }} />
              </div>
            </div>
          )}
          {isAvailable && showClashes && (
            <div className="ePanelTitle__legend">
              <span>
                <i className="is-clear" />
                Free
              </span>
              <span>
                <i className="is-partial" />
                Possible clash
              </span>
              <span>
                <i className="is-full" />
                Clash
              </span>
            </div>
          )}
          <div className="twoPanelEditorPanel__modernSearchRow">
            <label className="twoPanelEditorPanel__search">
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                onKeyDown={event => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  setAppliedSearch(search);
                  onSearchChange && onSearchChange(search);
                }}
                placeholder={
                  unitLabel === 'schools'
                    ? 'Search schools'
                    : isAvailable || searchByNameOnly
                    ? 'Search by name or surname'
                    : 'Search name, surname, class or house'
                }
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setAppliedSearch('');
                    onSearchChange && onSearchChange('');
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </label>
            {isAvailable && showFilterControl && (
              <button
                type="button"
                className={`twoPanelEditorPanel__modernFilterBtn${isShowFilter ? ' is-active' : ''}`}
                onClick={onFilterClick}
                aria-expanded={Boolean(isShowFilter)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
                Filters {activeFilterCount > 0 && <b>{activeFilterCount}</b>}
              </button>
            )}
            {isAvailable && isLoading && <span className="twoPanelEditorPanel__searchStatus">Updating…</span>}
            {searchExtra}
          </div>
          {isAvailable && showFilterControl && activeFilterCount > 0 && (
            <div className="twoPanelEditorPanel__chips">
              {filterDefinitions.reduce<React.ReactNode[]>((chips, definition) => {
                const activeValues = activeValuesFor(definition);
                const isCollapsibleGroup = definition.key === 'forms' || definition.key === 'houses';
                const isExpanded = expandedChipGroups.has(definition.key);
                const visibleValues = isCollapsibleGroup && !isExpanded ? activeValues.slice(0, 3) : activeValues;
                const definitionChips = visibleValues.map(value => {
                  const option = definition.options.find(candidate => String(candidate.value) === String(value));
                  const isPreset =
                    lockPresetFilters && (presetFilters[definition.key] || []).map(String).includes(String(value));
                  return (
                    <button
                      type="button"
                      key={`${definition.key}-${value}`}
                      className={isPreset ? 'is-preset' : ''}
                      onClick={() => !isPreset && toggleFilterValue(definition, value)}
                      disabled={isPreset}
                      title={isPreset ? `Set by the ${presetSourceLabel} and cannot be removed here` : undefined}
                    >
                      {isPreset && (
                        <svg className="twoPanelEditorPanel__chipLock" viewBox="0 0 24 24" aria-hidden="true">
                          <rect x="5.5" y="10" width="13" height="10" rx="2" />
                          <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" />
                        </svg>
                      )}
                      {definition.label}: {option ? option.text : value}
                      {isPreset ? '' : ' ×'}
                    </button>
                  );
                });
                if (isCollapsibleGroup && activeValues.length > 3) {
                  definitionChips.push(
                    <button
                      type="button"
                      key={`${definition.key}-chip-toggle`}
                      className="is-chip-toggle"
                      onClick={() =>
                        setExpandedChipGroups(current => {
                          const next = new Set(current);
                          if (next.has(definition.key)) next.delete(definition.key);
                          else next.add(definition.key);
                          return next;
                        })
                      }
                      aria-expanded={isExpanded}
                      title={
                        isExpanded
                          ? `Collapse ${definition.label.toLowerCase()}`
                          : `Show all ${definition.label.toLowerCase()}`
                      }
                    >
                      {isExpanded ? '−' : `+${activeValues.length - 3}`}
                    </button>
                  );
                }
                return chips.concat(definitionChips);
              }, [])}
              {birthdayFilter.from && (
                <button
                  type="button"
                  onClick={() => onFilterChange && onFilterChange({ target: { value: '' } }, 'birthday', 'FROM')}
                >
                  DOB from {birthdayFilter.from} ×
                </button>
              )}
              {birthdayFilter.to && (
                <button
                  type="button"
                  onClick={() => onFilterChange && onFilterChange({ target: { value: '' } }, 'birthday', 'TO')}
                >
                  DOB to {birthdayFilter.to} ×
                </button>
              )}
              {filters && filters.specialEducationNeeds && (
                <button
                  type="button"
                  onClick={() =>
                    onFilterChange && onFilterChange({ target: { value: false } }, 'specialEducationNeeds')
                  }
                >
                  SEN ×
                </button>
              )}
              {filters && filters.pupilsPremium && (
                <button
                  type="button"
                  onClick={() => onFilterChange && onFilterChange({ target: { value: false } }, 'pupilsPremium')}
                >
                  Pupil premium ×
                </button>
              )}
              {filtersDifferFromPreset && (
                <button
                  type="button"
                  className="is-clear"
                  onClick={() =>
                    Object.keys(presetFilters || {}).length && onReplaceFilters
                      ? onReplaceFilters(presetFilters)
                      : onClearFilterClick()
                  }
                >
                  Reset filters
                </button>
              )}
            </div>
          )}
          {isAvailable && showFilterControl && isShowFilter && (
            <>
              <button
                type="button"
                className="twoPanelEditorPanel__filterScrim"
                aria-label="Close filters"
                onClick={() => onFilterClick && onFilterClick({ preventDefault: () => {} })}
              />
              <form
                className="twoPanelEditorPanel__filterPopover"
                role="dialog"
                aria-modal="true"
                aria-label={`${title || 'List'} filters`}
                onSubmit={event => {
                  event.preventDefault();
                  if (onReplaceFilters) onReplaceFilters(draftFilters);
                  else onApplyFilterClick();
                  onFilterClick && onFilterClick({ preventDefault: () => {} });
                }}
              >
                <div className="twoPanelEditorPanel__filterPopoverHead">
                  <strong>Filters</strong>
                  <button
                    type="button"
                    aria-label="Close filters"
                    onClick={() => onFilterClick && onFilterClick({ preventDefault: () => {} })}
                  >
                    ×
                  </button>
                </div>
                <div className="twoPanelEditorPanel__filterPopoverBody">
                  {savedFilterPresets.length > 0 && (
                    <fieldset className="twoPanelEditorPanel__savedFilters">
                      <legend>Saved filters</legend>
                      <div>
                        {savedFilterPresets.map((preset, index) => (
                          <span key={`${preset.name}-${index}`}>
                            <button
                              type="button"
                              onClick={() => {
                                setDraftFilters(JSON.parse(JSON.stringify(preset.filters)));
                                onReplaceFilters && onReplaceFilters(preset.filters);
                              }}
                            >
                              {preset.name}
                            </button>
                            <button
                              type="button"
                              aria-label={`Delete ${preset.name}`}
                              onClick={() =>
                                setSavedFilterPresets(current =>
                                  current.filter((_, currentIndex) => currentIndex !== index)
                                )
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  {filterDefinitions.map(renderFilterDefinition)}
                  {hasBirthdayFilter && (
                    <fieldset>
                      <legend>Date of birth</legend>
                      <SitDatePicker
                        className="twoPanelEditorPanel__datePicker"
                        mode="range"
                        value={{
                          start: getDateFilterValue(drawerBirthdayFilter.from),
                          end: getDateFilterValue(drawerBirthdayFilter.to)
                        }}
                        onChange={(nextValue: any) =>
                          setDraftFilters(current => ({
                            ...current,
                            birthday: {
                              from: nextValue && nextValue.start ? moment(nextValue.start).format('DD-MM-YYYY') : '',
                              to: nextValue && nextValue.end ? moment(nextValue.end).format('DD-MM-YYYY') : ''
                            }
                          }))
                        }
                        format="DD-MM-YYYY"
                        placeholder="Select date range"
                        numberOfMonths={2}
                        align="right"
                        clearable
                        size="sm"
                      />
                    </fieldset>
                  )}
                  {(hasSenFilter || hasPupilPremiumFilter) && (
                    <fieldset>
                      <legend>Other</legend>
                      {hasSenFilter && (
                        <label>
                          <input
                            type="checkbox"
                            checked={Boolean(drawerFilters && drawerFilters.specialEducationNeeds)}
                            onChange={event =>
                              setDraftFilters(current => ({ ...current, specialEducationNeeds: event.target.checked }))
                            }
                          />
                          <span>SEN</span>
                        </label>
                      )}
                      {hasPupilPremiumFilter && (
                        <label>
                          <input
                            type="checkbox"
                            checked={Boolean(drawerFilters && drawerFilters.pupilsPremium)}
                            onChange={event =>
                              setDraftFilters(current => ({ ...current, pupilsPremium: event.target.checked }))
                            }
                          />
                          <span>Pupil premium</span>
                        </label>
                      )}
                    </fieldset>
                  )}
                  <button
                    type="button"
                    className="twoPanelEditorPanel__saveFilter"
                    onClick={saveCurrentFilter}
                    disabled={!drawerFilterCount}
                    title={!drawerFilterCount ? 'Choose at least one filter first' : undefined}
                  >
                    <span aria-hidden="true">▣</span> Save this filter as…
                  </button>
                </div>
                <div className="twoPanelEditorPanel__filterPopoverFoot">
                  <button type="button" className="twoPanelEditorPanel__clearFilters" onClick={resetDraftFilters}>
                    Clear all
                  </button>
                  <div>
                    <button type="button" onClick={() => onFilterClick && onFilterClick({ preventDefault: () => {} })}>
                      Cancel
                    </button>
                    <button type="submit" className="twoPanelEditorPanel__applyFilters">
                      Apply
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="twoPanelEditorPanel__modernSubhead">
          {isLoading && loadingMessage && (
            <div className="twoPanelEditorPanel__bulkStatus" role="status" aria-live="polite">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              <span>{loadingMessage}</span>
            </div>
          )}
          <div className="twoPanelEditorPanel__selectionSummary">
            <SelectAllCheckbox
              compact={true}
              onSelectAllClick={
                onSelectVisibleItems
                  ? () => onSelectVisibleItems(filteredItems)
                  : onSelectAllFilteredClick || onSelectAllClick
              }
              onSelectAllOnPageClick={
                onSelectVisibleItems ? () => onSelectVisibleItems(displayedItems) : onSelectAllOnPageClick
              }
              onUnselectAllClick={onUnselectAllClick}
              isChecked={
                displayedItems.length > 0 &&
                displayedItems.every(item => itemsSelected.some(selected => isSameTwoPanelItem(selected, item)))
              }
              isSelectDisabled={isLoading}
              totalCount={!onSearchChange && normalizedSearch ? filteredItems.length : total}
              currentPage={currentPage}
              selectedItems={itemsSelected}
              pageItemsCount={displayedItems.length}
            />
            <span>
              {itemsSelected.length
                ? `${itemsSelected.length} checked`
                : normalizedSearch || activeFilterCount
                ? `${displayedItems.length} of ${total} match${normalizedSearch ? ` “${appliedSearch.trim()}”` : ''}${
                    activeFilterCount ? ` + ${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'}` : ''
                  }`
                : `${displayedItems.length} of ${total} shown`}
            </span>
          </div>
        </div>
        <div className="twoPanelEditorPanel__personList">
          {displayedItems.length ? (
            displayedItems.map(renderTransferRow)
          ) : (
            <div className="twoPanelEditorPanel__modernEmpty">
              <strong>
                {isAvailable && isLoading
                  ? 'Searching…'
                  : !isAvailable && items.length === 0
                  ? 'No students selected yet'
                  : 'No matches for the current search/filters.'}
              </strong>
              {isAvailable && isLoading && <span>Checking all eligible students for this search.</span>}
              {!isAvailable && items.length === 0 && (
                <span>Select students from the available list to add them here.</span>
              )}
              {isAvailable && !isLoading && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setAppliedSearch('');
                    onSearchChange && onSearchChange('');
                    onClearFilterClick();
                  }}
                >
                  Clear search &amp; filters
                </button>
              )}
              {!isAvailable && items.length > 0 && normalizedSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setAppliedSearch('');
                  }}
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
        {(isAvailable || total > items.length) && (
          <div className="twoPanelEditorPanel__footer">
            <Pagination2
              currentPage={currentPage}
              setCurrentPageParams={setCurrentPageParams}
              total={total}
              limit={paginationLimit}
              modern={isSourceFirst || modernPagination}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="twoPanelEditorPanel">
      {title && (
        <div className="ePanelTitle">
          <div>
            <span className="ePanelTitle__text">
              <span>{title}</span>
              <span className="ePanelTitle__count">{total}</span>
            </span>
            {headerExtra && <div className="twoPanelEditorPanel__headerExtra">{headerExtra}</div>}
            {!isNoVisibleBlock && columns.some(column => column.field === 'clashes') && (
              <div className="ePanelTitle__legend" aria-label="Clash status legend">
                <span>
                  <i className="is-clear" />
                  Clear
                </span>
                <span>
                  <i className="is-partial" />
                  Partial clash
                </span>
                <span>
                  <i className="is-full" />
                  Full clash
                </span>
              </div>
            )}
          </div>
          {allColumns && hiddenFields && onToggleColumn && isColumnToggleable && (
            <ColumnToggleButton
              columns={allColumns}
              hiddenFields={hiddenFields}
              onToggle={onToggleColumn}
              isToggleable={isColumnToggleable}
            />
          )}
        </div>
      )}

      <div className="twoPanelEditorPanel__toolbar">
        <div className="twoPanelEditorPanel__toolbarLeft">
          <label className="twoPanelEditorPanel__search">
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                setAppliedSearch(search);
                onSearchChange && onSearchChange(search);
              }}
              placeholder={isNoVisibleBlock ? 'Search selected' : 'Search students'}
              aria-label={isNoVisibleBlock ? 'Search selected participants' : 'Search available students'}
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setAppliedSearch('');
                  onSearchChange && onSearchChange('');
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </label>

          {!isNoVisibleBlock && (
            <div className="twoPanelEditorPanel__filterBtn">
              <FilterIcon onTableFilterClick={onFilterClick} isFilterShown={isShowFilter} isDataFiltered={isFiltered} />
            </div>
          )}
        </div>

        <div className="twoPanelEditorPanel__toolbarRight" />
      </div>

      {!isNoVisibleBlock && (
        <div className="twoPanelEditorPanel__selectionBar">
          <SelectAllCheckbox
            onSelectAllClick={onSelectAllClick}
            onSelectAllOnPageClick={onSelectAllOnPageClick}
            onUnselectAllClick={onUnselectAllClick}
            isChecked={isSelectAllChecked}
            totalCount={total}
            currentPage={currentPage}
            selectedItems={itemsSelected}
            isNoVisibleCheckbox={false}
          />
          <span>{itemsSelected.length ? `${itemsSelected.length} selected` : `${filteredItems.length} shown`}</span>
        </div>
      )}

      <div className="twoPanelEditorPanel__tableWrap">
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th />
              {isSourceFirst && <th className="twoPanelEditorPanel__avatarHead" aria-label="Photo" />}
              {renderHead}
            </tr>

            {isShowFilter && (
              <>
                <tr className="twoPanelEditorPanel__filterRow">
                  <FilterRow2
                    columns={visibleColumns}
                    filters={filters}
                    onTableFilterChange={onFilterChange}
                    onApplyFilterClick={onApplyFilterClick}
                    options={filterOptions}
                  />
                </tr>
                <tr className="twoPanelEditorPanel__filterButtonsRow">
                  <GridFilterButtons
                    onApplyFilterClick={onApplyFilterClick}
                    onClearFilterClick={onClearFilterClick}
                    colSpanNumber={colSpanNumber}
                  />
                </tr>
              </>
            )}
          </thead>
          <tbody>
            {renderBody}
            {!renderBody.length && (
              <tr>
                <td colSpan={colSpanNumber} className="twoPanelEditorPanel__empty">
                  {isLoading ? 'Loading...' : 'No items to display'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isSourceFirst && !isNoVisibleBlock && (
        <div className="twoPanelEditorPanel__footer">
          <Pagination2
            currentPage={currentPage}
            setCurrentPageParams={setCurrentPageParams}
            total={total}
            limit={paginationLimit}
            modern={isSourceFirst || modernPagination}
          />
        </div>
      )}
    </div>
  );
};

Panel.displayName = 'Panel';
